import { Route } from '@angular/router';
import {
  buildPortalDashboardChildRoutes,
  buildPortalPermissionGuard,
} from '@dlap-ui/shared-forms/shell/portal-shell/portal-shell';
import { authGuard, ERole } from '@dlap-ui/core';
import type { PortalAccessConfig } from '@dlap-ui/shared-forms/auth/otp-form/otp-form';
import { environment } from '../environments/environment';
import { LAW_FIRM_PORTAL_CONFIG } from './pages/dashboard-layout/portal.config';

const dashboardChildRoutes: Route[] = buildPortalDashboardChildRoutes(LAW_FIRM_PORTAL_CONFIG);

const LAW_FIRM_PORTAL_ACCESS: PortalAccessConfig = {
  allowedCategories: ['Law Firm'],
  missingCategoryRoles: [ERole.CONVEYANCER, ERole.ASSOCIATION_ADMIN, ERole.MEMBER_ADMIN, ERole.NOTARY],
  categoryRedirects: {
    deeds: environment.portalUrls.deeds,
    bank: environment.portalUrls.bank,
    'bond holding': environment.portalUrls.bank,
    'high court': environment.portalUrls.highCourt,
  },
  unauthorizedMessage: 'Not allowed to use this portal.',
};

export const appRoutes: Route[] = [
  {
    path: '',
    loadComponent: () =>
      import('./pages/landing/landing').then((m) => m.Landing),
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('./pages/legal/privacy-policy/privacy-policy').then((m) => m.PrivacyPolicy),
  },
  {
    path: 'terms',
    loadComponent: () => import('./pages/legal/terms/terms').then((m) => m.Terms),
  },
  {
    path: 'accessibility',
    loadComponent: () => import('./pages/legal/accessibility/accessibility').then((m) => m.Accessibility),
  },
  {
    path: 'auth',
    loadComponent: () =>
      import('./pages/auth-layout/auth-layout').then((m) => m.AuthLayout),
    children: [
      {
        path: 'login',
        loadComponent: () =>
          import('./pages/login/login').then((m) => m.Login),
      },
      {
        path: 'otp',
        data: {
          production: environment.production,
          successRoute: '/overview/applications',
          portalAccess: LAW_FIRM_PORTAL_ACCESS,
        },
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/otp-form/otp-form').then((m) => m.OtpForm),
      },
      {
        path: 'forgot-password',
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/forgot-password-form/forgot-password-form').then((m) => m.ForgotPasswordForm),
      },
      {
        path: 'reset-password',
        loadComponent: () =>
          import('@dlap-ui/shared-forms/auth/reset-password-form/reset-password-form').then((m) => m.ResetPasswordForm),
      },
      {
        path: 'apply/member-admin',
        data: { mode: 'member-admin' },
        loadComponent: () =>
          import('./pages/apply-membership/apply-membership').then((m) => m.ApplyMembership),
      },
      {
        path: 'apply/conveyancer',
        data: { mode: 'conveyancer' },
        loadComponent: () =>
          import('./pages/apply-membership/apply-membership').then((m) => m.ApplyMembership),
      },
      { path: '', redirectTo: 'login', pathMatch: 'full' },
    ],
  },
  {
    path: 'payment',
    children: [
      {
        path: 'service/:feeType',
        loadComponent: () =>
          import('@dlap-ui/shared').then((m) => m.Payments),
      },
      {
        path: 'service/:feeType/:userId',
        loadComponent: () =>
          import('@dlap-ui/shared').then((m) => m.Payments),
      },
    ],
  },
  {
    path: 'overview',
    loadComponent: () =>
      import('./pages/dashboard-layout/dashboard-layout').then(
        (m) => m.DashboardLayout,
      ),
    canActivate: [authGuard],
    children: [
      ...dashboardChildRoutes,
      {
        path: 'apply/reissuance',
        loadComponent: () =>
          import('./pages/apply/reissuance/reissuance').then(
            (m) => m.Reissuance,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/transfer',
        loadComponent: () =>
          import('./pages/apply/transfer/transfer').then((m) => m.Transfer),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/bond-registration',
        loadComponent: () =>
          import('./pages/apply/bond-registration/bond-registration').then(
            (m) => m.BondRegistration,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/sectional-title',
        loadComponent: () =>
          import('./pages/apply/sectional-title/sectional-title').then((m) => m.SectionalTitle),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/trust-registration',
        loadComponent: () =>
          import('./pages/apply/trust-registration/trust-registration').then(
            (m) => m.TrustRegistration,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/notarial-deed-section',
        loadComponent: () =>
          import('./pages/apply/notarial-deed-section/notarial-deed-section').then(
            (m) => m.NotarialDeedSection,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/consolidation',
        loadComponent: () =>
          import('./pages/apply/consolidation/consolidation').then((m) => m.Consolidation),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/subdivision',
        loadComponent: () =>
          import('./pages/apply/subdivision/subdivision').then((m) => m.Subdivision),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/estate',
        loadComponent: () => import('./pages/apply/estate/estate').then((m) => m.Estate),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/bond-cancellation',
        loadComponent: () =>
          import('./pages/apply/bond-cancellation/bond-cancellation').then(
            (m) => m.BondCancellation,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/land-tenure-subdivision',
        loadComponent: () =>
          import('./pages/apply/land-tenure-subdivision/land-tenure-subdivision').then(
            (m) => m.LandTenureSubdivision,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      {
        path: 'apply/land-tenure-transfer',
        loadComponent: () =>
          import('./pages/apply/land-tenure-transfer/land-tenure-transfer').then(
            (m) => m.LandTenureTransfer,
          ),
        canActivate: buildPortalPermissionGuard(LAW_FIRM_PORTAL_CONFIG, 'applications'),
      },
      { path: '', redirectTo: 'applications', pathMatch: 'full' },
    ],
  },
];
